import { env } from '@/env';

export const MANAGEMENT_ORGANIZATION = 'nvx-management';

/**
 * Routes that belong to the application but may arrive without a tenant
 * prefix (for example an old bookmark to `/inbound`). Everything else is
 * interpreted as `/:tenantCode/...`.
 */
export const BARE_APPLICATION_SEGMENTS = new Set([
  'dashboard',
  'profile',
  'road-transport',
  'inbound',
  'outbound',
  'stock',
  'billing',
  'containers',
  'address-book',
  'admin',
  'announcements',
]);

const PUBLIC_SEGMENTS = new Set(['login', 'unauthorized', 'auth']);
const TENANT_CODE_PATTERN = /^[a-z0-9][a-z0-9_-]{0,49}$/;
const AUTH0_ORGANIZATION_ID_PATTERN = /^org_[A-Za-z0-9]+$/;

export interface ParsedEntryUrl {
  path: string;
  pathWithQueryAndHash: string;
  firstSegment: string | null;
  requestedTenantCode: string | null;
  isRoot: boolean;
  isLogin: boolean;
  isAuthCallback: boolean;
  isPublic: boolean;
  isBareApplicationPath: boolean;
  invitation: string | null;
  invitationOrganization: string | null;
  loginAsTenantCode: string | null;
  hasLoginAsTenantParameter: boolean;
}

export function normalizeTenantCode(value: string | null | undefined): string | null {
  const normalized = value?.trim().toLowerCase() ?? '';
  return TENANT_CODE_PATTERN.test(normalized) ? normalized : null;
}

/**
 * Auth0 organization IDs are case-sensitive opaque identifiers, while Auth0
 * organization names follow the same lowercase format as tenant codes.
 */
export function normalizeAuth0Organization(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim() ?? '';
  if (AUTH0_ORGANIZATION_ID_PATTERN.test(trimmed)) return trimmed;
  return normalizeTenantCode(trimmed);
}

export function parseEntryUrl(location: Pick<Location, 'pathname' | 'search' | 'hash'>): ParsedEntryUrl {
  const segments = location.pathname.split('/').filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase() ?? null;
  const isRoot = firstSegment == null;
  const isLogin = firstSegment === 'login';
  const isAuthCallback = location.pathname === '/auth/callback';
  const isPublic = firstSegment != null && PUBLIC_SEGMENTS.has(firstSegment);
  const isBareApplicationPath =
    isRoot || (firstSegment != null && BARE_APPLICATION_SEGMENTS.has(firstSegment));
  const requestedTenantCode =
    !isPublic && !isBareApplicationPath ? normalizeTenantCode(firstSegment) : null;
  const params = new URLSearchParams(location.search);

  return {
    path: location.pathname,
    pathWithQueryAndHash: `${location.pathname}${location.search}${location.hash}`,
    firstSegment,
    requestedTenantCode,
    isRoot,
    isLogin,
    isAuthCallback,
    isPublic,
    isBareApplicationPath,
    invitation: params.get('invitation'),
    invitationOrganization: normalizeAuth0Organization(params.get('organization')),
    loginAsTenantCode: normalizeTenantCode(params.get('tenant_code')),
    hasLoginAsTenantParameter: params.has('tenant_code'),
  };
}

export function tenantPath(tenantCode: string, path: string): string {
  const normalized = normalizeTenantCode(tenantCode);
  if (!normalized) return '/';
  if (path === '/' || path === '') return `/${normalized}/dashboard`;
  return `/${normalized}${path.startsWith('/') ? path : `/${path}`}`;
}

export function getAdminPortalUrl(): string {
  const environment = env.ENVIRONMENT.trim().toLowerCase();
  if (environment === 'production' || environment === 'prod') {
    return 'https://admin.nvx.ai';
  }

  // Runtime configuration uses "development", while NVX hostnames use "dev".
  const hostnameEnvironment = environment === 'development' ? 'dev' : environment;
  return `https://admin.${hostnameEnvironment || 'dev'}.nvx.ai`;
}

/** Only same-origin relative application paths may be restored after Auth0. */
export function safeReturnTo(value: string | undefined): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/';

  try {
    const target = new URL(value, window.location.origin);
    if (target.origin !== window.location.origin) return '/';
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return '/';
  }
}
